import { useState, useEffect } from "react"
import { FileText, ShoppingCart, AlertTriangle, Scale, Mail, ArrowRight, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Link } from "react-router-dom"
import Seo from "@/seo/seo"
import { routeSeoConfig } from "@/utils/seo-config"
import axios from "@/utils/axios"

// Icon mapping
const iconMap = {
  FileText,
  ShoppingCart,
  AlertTriangle,
  Scale,
  Mail,
}

const defaultSections = [
    {
      title: "Account Terms",
      icon: ShoppingCart,
      content: [
        "You must be 18 years or older to create an account and make purchases",
        "You are responsible for maintaining the security of your account credentials",
        "You must provide accurate and complete information when creating your account",
        "One account per person - multiple accounts are not permitted",
        "We reserve the right to suspend or terminate accounts that violate these terms",
        "You are responsible for all activities that occur under your account",
      ],
    },
    {
      title: "Product Information & Orders",
      icon: FileText,
      content: [
        "We strive to display product information accurately, but cannot guarantee 100% accuracy",
        "Product colors may vary slightly due to monitor settings and lighting conditions",
        "All orders are subject to acceptance and product availability",
        "We reserve the right to limit quantities and refuse orders at our discretion",
        "Prices are subject to change without notice until payment is processed",
        "Order confirmation does not guarantee product availability",
      ],
    },
    {
      title: "Payment & Billing",
      icon: Scale,
      content: [
        "Payment is required at the time of purchase using accepted payment methods",
        "All prices are listed in Nigerian Naira (NGN) unless otherwise specified",
        "You authorize us to charge your payment method for all purchases",
        "Billing disputes must be reported within 60 days of the charge",
        "We use secure, encrypted payment processing for all transactions",
        "Promotional codes and discounts cannot be combined unless specified",
      ],
    },
    {
      title: "Prohibited Uses",
      icon: AlertTriangle,
      content: [
        "Using our website for any unlawful purpose or illegal activity",
        "Attempting to gain unauthorized access to our systems or user accounts",
        "Transmitting viruses, malware, or other harmful computer code",
        "Harassing, abusing, or harming other users or our staff",
        "Posting false, misleading, or defamatory content in reviews",
        "Reselling our products without written authorization",
      ],
    },
  ]

export default function TermsOfService() {
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get("/legal/terms")
        setPageData(res.data.page)
      } catch (error) {
        console.error("Error fetching terms of service:", error)
        setPageData(null)
      } finally {
        setLoading(false)
      }
    }
    fetchPage()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  const sections = pageData?.sections || defaultSections
  const heroTitle = pageData?.hero_title || "Terms of Service"
  const heroSubtitle = pageData?.hero_subtitle || "Please read these terms carefully before using our website or purchasing our products. By using our services, you agree to be bound by these terms."
  const heroBadgeText = pageData?.hero_badge_text || "Legal Terms"
  
  // Format lastUpdated to show only date in "December 15, 2024" format
  const formatDate = (dateString) => {
    if (!dateString) return "December 15, 2024"
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return dateString // Return as-is if invalid date
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    } catch {
      return dateString || "December 15, 2024"
    }
  }
  const lastUpdated = formatDate(pageData?.last_updated)
  const introductionTitle = pageData?.introduction_title || "Agreement to Terms"
  const introductionContent = pageData?.introduction_content || "These Terms of Service govern your use of our website."
  const additionalTerms = pageData?.additional_content?.additional_terms || []
  const contactTitle = pageData?.contact_title || "Questions About These Terms?"
  const contactSubtitle = pageData?.contact_subtitle || "If you have any questions about these Terms of Service, please contact us and we'll be happy to help."
  const contactEmail = pageData?.contact_email || "legal@fayozltd.com"

  return (
    <>
      <Seo {...routeSeoConfig.terms} type="website" />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {heroBadgeText && (
              <div className="inline-flex items-center gap-2 bg-secondary/20 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <FileText className="w-4 h-4" />
                {heroBadgeText}
              </div>
            )}
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 text-balance">
              {heroTitle}
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto text-pretty">
              {heroSubtitle}
            </p>
            <p className="text-sm text-gray-500">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <Card className="mb-12 border-0 shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{introductionTitle}</h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {introductionContent}
                </p>
              </CardContent>
            </Card>

            {/* Terms Sections */}
            <div className="space-y-8">
              {sections.map((section, index) => {
                const IconComponent = iconMap[section.icon] || FileText
                return (
                  <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <CardHeader className="pb-6">
                      <CardTitle className="flex items-center gap-3 text-2xl text-gray-900">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        {section.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {section.content.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start gap-3">
                            <div className="w-2 h-2 bg-secondary rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-600 leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )
              })}
            </div>

            {/* Additional Terms */}
            {additionalTerms.length > 0 && (
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                {additionalTerms.map((term, index) => (
                  <Card key={index} className="border-0 shadow-lg">
                    <CardHeader>
                      <CardTitle className="text-xl text-gray-900">{term.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 text-sm leading-relaxed">{term.content}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Contact Section */}
            <Card className="mt-12 border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <Mail className="w-12 h-12 text-secondary mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{contactTitle}</h3>
                {contactSubtitle && (
                  <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                    {contactSubtitle}
                  </p>
                )}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact-us">
                    <Button size="lg" className="bg-primary hover:bg-primary/90 text-white">
                      Contact Us
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  {contactEmail && (
                    <Button
                      variant="outline"
                      size="lg"
                      className="border-primary text-primary hover:bg-primary/5 bg-transparent"
                    >
                      Email: {contactEmail}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}
